import { ListLoading, ListLoadingType, PaginationModel, SortingModel } from '@shared-util-types';
import { SortDirection } from '@data-access';
import dayjs from 'dayjs';

export class StoredComponentHelperService {
  static cleanPaginationResponse(pagination?: PaginationModel): PaginationModel {
    return {
      first: pagination?.first as boolean,
      last: pagination?.last as boolean,
      number: pagination?.number as number,
      size: pagination?.size as number,
      elements: pagination?.elements as number,
      pages: pagination?.pages as number,
    };
  }

  static setNextPage(pagination?: PaginationModel, elementsLength?: number): number {
    const pageSize: number = pagination?.size ? pagination.size : 0;
    const storedEntitiesCount = elementsLength as number;
    return (storedEntitiesCount - (storedEntitiesCount % pageSize)) / pageSize;
  }

  static setTotalElements(
    elementId: string,
    pagination?: PaginationModel,
    ids?: string[] | number[],
    isDelete?: boolean,
  ): number {
    let totalElements: number = pagination?.elements ? pagination.elements : 0;

    if (isDelete && totalElements && ids && ids.some((el) => el == elementId)) {
      totalElements -= 1;
    }

    if (!isDelete && (!ids || !ids?.some((el) => el == elementId))) {
      totalElements += 1;
    }

    return totalElements;
  }

  static updateListLoading(
    actionPayload: { loading: ListLoadingType; clearState: boolean },
    ids?: string[] | number[],
  ): ListLoadingType | undefined {
    return actionPayload.loading == ListLoading.List && ids?.length && !actionPayload.clearState
      ? undefined
      : actionPayload.loading;
  }

  static updateItemLoading(
    loadingItems: string[] | undefined,
    id: string,
    isStart: boolean,
  ): string[] {
    const items = loadingItems ? [...loadingItems] : [];
    if (isStart) {
      items.push(id);
    } else {
      return items.filter((el) => el != id);
    }

    return items;
  }

  static resolveSorting(
    current: SortingModel | undefined,
    next: SortingModel | undefined,
  ): SortingModel {
    if (!next?.key) {
      return {};
    }

    if (!current?.key || current.key !== next.key) {
      return {
        key: next.key,
        direction: next.direction || 'ASC',
      };
    }

    if (next.direction && current.direction !== next.direction) {
      return {
        key: next.key,
        direction: next.direction,
      };
    }

    return {};
  }

  static sortByNumber<T>(
    items: T[],
    selector: (item: T) => number | undefined | null,
    direction: SortDirection,
  ): T[] {
    return [...items].sort((a, b) => {
      const valA = selector(a);
      const valB = selector(b);

      const isANull = valA === undefined || valA === null;
      const isBNull = valB === undefined || valB === null;

      if (isANull && isBNull) return 0;
      if (isANull) return 1;
      if (isBNull) return -1;

      const comparison = valA - valB;

      return direction === SortDirection.Desc ? -comparison : comparison;
    });
  }

  static sortByString<T>(
    items: T[],
    selector: (item: T) => string | undefined | null,
    direction: SortDirection,
  ): T[] {
    return [...items].sort((a, b) => {
      const valA = selector(a);
      const valB = selector(b);

      if (!valA && !valB) return 0;
      if (!valA) return 1;
      if (!valB) return -1;

      const comparison = valA.localeCompare(valB);

      return direction === SortDirection.Desc ? -comparison : comparison;
    });
  }

  static sortByDate<T>(
    items: T[],
    selector: (item: T) => string | undefined,
    direction: SortDirection,
  ): T[] {
    return [...items].sort((a, b) => {
      const dateA = selector(a);
      const dateB = selector(b);

      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      const timeA = dayjs(dateA).isValid() ? dayjs(dateA).valueOf() : 0;
      const timeB = dayjs(dateB).isValid() ? dayjs(dateB).valueOf() : 0;

      if (timeA === 0 && timeB === 0) return 0;
      if (timeA === 0) return 1;
      if (timeB === 0) return -1;

      const comparison = timeA - timeB;

      return direction === SortDirection.Desc ? -comparison : comparison;
    });
  }

  static sortByEnum<T>(
    items: T[],
    selector: (item: T) => string | undefined | null,
    direction: SortDirection,
    enumOrder: string[],
  ): T[] {
    const enumRank = new Map(enumOrder.map((status, index) => [status, index]));

    return [...items].sort((a, b) => {
      const valA = selector(a);
      const valB = selector(b);

      const isAInvalid = !valA || !enumRank.has(valA);
      const isBInvalid = !valB || !enumRank.has(valB);

      if (isAInvalid && isBInvalid) return 0;
      if (isAInvalid) return 1;
      if (isBInvalid) return -1;

      const rankA = enumRank.get(valA)!;
      const rankB = enumRank.get(valB)!;

      const comparison = rankA - rankB;

      return direction === SortDirection.Desc ? -comparison : comparison;
    });
  }

  static placeItem(ids: string[], itemId: string, afterItemId?: string | null): string[] {
    if (
      afterItemId === undefined ||
      (typeof afterItemId === 'string' && !ids.includes(afterItemId))
    ) {
      return ids;
    }

    const next = ids.filter((id) => id !== itemId);

    if (afterItemId === null) {
      next.unshift(itemId);
      return next;
    }

    const afterIndex = next.indexOf(afterItemId);
    next.splice(afterIndex + 1, 0, itemId);

    return next;
  }
}
